import React, { useState, useEffect } from 'react';
import './UserSettings.css';
import { getDropdownOptions, setDropdownOptions, getDefaultOptions } from '../utils/dropdownConfig';

function UserSettings() {
  // Dropdown configurations
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [integratorOptions, setIntegratorOptions] = useState([]);

  // New option inputs
  const [newStatusOption, setNewStatusOption] = useState('');
  const [newTypeOption, setNewTypeOption] = useState('');
  const [newIntegratorOption, setNewIntegratorOption] = useState('');

  // Expanded sections
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    // Load dropdown configurations from API
    const loadOptions = async () => {
      try {
        const [status, type, integrator] = await Promise.all([
          getDropdownOptions('status'),
          getDropdownOptions('type'),
          getDropdownOptions('integrator'),
        ]);
        setStatusOptions(status);
        setTypeOptions(type);
        setIntegratorOptions(integrator);
      } catch (error) {
        console.error('Error loading dropdown options:', error);
      }
    };
    loadOptions();
  }, []);

  const handleAddOption = async (type) => {
    let newOption = '';
    let currentOptions = [];
    let setOptions = null;
    let setNewOption = null;

    if (type === 'status') {
      newOption = newStatusOption;
      currentOptions = statusOptions;
      setOptions = setStatusOptions;
      setNewOption = setNewStatusOption;
    } else if (type === 'type') {
      newOption = newTypeOption;
      currentOptions = typeOptions;
      setOptions = setTypeOptions;
      setNewOption = setNewTypeOption;
    } else if (type === 'integrator') {
      newOption = newIntegratorOption;
      currentOptions = integratorOptions;
      setOptions = setIntegratorOptions;
      setNewOption = setNewIntegratorOption;
    }

    if (newOption.trim() && !currentOptions.includes(newOption.trim())) {
      const updated = [...currentOptions, newOption.trim()];
      setOptions(updated);
      await setDropdownOptions(type, updated);
      setNewOption('');
    }
  };

  const handleRemoveOption = async (type, option) => {
    let currentOptions = [];
    let setOptions = null;

    if (type === 'status') {
      currentOptions = statusOptions;
      setOptions = setStatusOptions;
    } else if (type === 'type') {
      currentOptions = typeOptions;
      setOptions = setTypeOptions;
    } else if (type === 'integrator') {
      currentOptions = integratorOptions;
      setOptions = setIntegratorOptions;
    }

    const updated = currentOptions.filter((o) => o !== option);
    setOptions(updated);
    await setDropdownOptions(type, updated);
  };

  const handleResetDefaults = async (type) => {
    const defaults = getDefaultOptions(type);

    if (type === 'status') {
      setStatusOptions(defaults);
      await setDropdownOptions('status', defaults);
    } else if (type === 'type') {
      setTypeOptions(defaults);
      await setDropdownOptions('type', defaults);
    } else if (type === 'integrator') {
      setIntegratorOptions(defaults);
      await setDropdownOptions('integrator', defaults);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="settings-page">
      <div className="card">
        <h2>Dropdown Configuration</h2>
        <p className="description">
          Customize the options available in dropdown menus throughout the application.
        </p>

        {/* Status Options */}
        <div className="config-section">
          <div className="config-header" onClick={() => toggleSection('status')}>
            <h3>Status Options</h3>
            <span className="toggle-icon">{expandedSection === 'status' ? '▼' : '▶'}</span>
          </div>
          {expandedSection === 'status' && (
            <div className="config-content">
              <div className="options-list">
                {statusOptions.map((option, index) => (
                  <div key={index} className="option-item">
                    <span>{option}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveOption('status', option)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="add-option">
                <input
                  type="text"
                  value={newStatusOption}
                  onChange={(e) => setNewStatusOption(e.target.value)}
                  placeholder="Add new status option"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption('status'))}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAddOption('status')}
                >
                  Add
                </button>
              </div>
              <button
                type="button"
                className="btn-reset"
                onClick={() => handleResetDefaults('status')}
              >
                Reset to Defaults
              </button>
            </div>
          )}
        </div>

        {/* Type Options */}
        <div className="config-section">
          <div className="config-header" onClick={() => toggleSection('type')}>
            <h3>Type Options</h3>
            <span className="toggle-icon">{expandedSection === 'type' ? '▼' : '▶'}</span>
          </div>
          {expandedSection === 'type' && (
            <div className="config-content">
              <div className="options-list">
                {typeOptions.map((option, index) => (
                  <div key={index} className="option-item">
                    <span>{option}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveOption('type', option)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="add-option">
                <input
                  type="text"
                  value={newTypeOption}
                  onChange={(e) => setNewTypeOption(e.target.value)}
                  placeholder="Add new type option"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption('type'))}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAddOption('type')}
                >
                  Add
                </button>
              </div>
              <button
                type="button"
                className="btn-reset"
                onClick={() => handleResetDefaults('type')}
              >
                Reset to Defaults
              </button>
            </div>
          )}
        </div>

        {/* Integrator Options */}
        <div className="config-section">
          <div className="config-header" onClick={() => toggleSection('integrator')}>
            <h3>Integrator Options</h3>
            <span className="toggle-icon">{expandedSection === 'integrator' ? '▼' : '▶'}</span>
          </div>
          {expandedSection === 'integrator' && (
            <div className="config-content">
              <div className="options-list">
                {integratorOptions.map((option, index) => (
                  <div key={index} className="option-item">
                    <span>{option}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveOption('integrator', option)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="add-option">
                <input
                  type="text"
                  value={newIntegratorOption}
                  onChange={(e) => setNewIntegratorOption(e.target.value)}
                  placeholder="Add new integrator option"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption('integrator'))}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAddOption('integrator')}
                >
                  Add
                </button>
              </div>
              <button
                type="button"
                className="btn-reset"
                onClick={() => handleResetDefaults('integrator')}
              >
                Reset to Defaults
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h2>About</h2>
        <p>
          <strong>Plant Patrol</strong>
        </p>
        <p>Version: 1.0.0</p>
        <p>
          Plant Control management system for tracking client integrations and workflow progress.
        </p>
      </div>
    </div>
  );
}

export default UserSettings;
